/**
 * Helpers for the TPO's editable requirement scores on a campus job posting.
 *
 * The scores drive the "Job Requirement" series of the Student Dashboard's Skill
 * Gap graph, so the TPO has to be able to see what was generated and override it.
 * A score is only authoritative when it carries a source: `tpo` (typed by the
 * TPO) or `ai` (a suggestion the TPO accepted and submitted). Anything else is
 * sent without a source so the backend regenerates it - that is what keeps the
 * old flat 8.0/6.5 placeholders from locking the calibration.
 */

export const SKILL_SCORE_MIN = 1;
export const SKILL_SCORE_MAX = 10;

/** Split a comma-separated / array field into unique, trimmed skill names. */
export function parseSkillNames(value) {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : String(value).split(',');
  const seen = new Set();
  const names = [];
  raw.forEach((item) => {
    const name = String(
      typeof item === 'object' && item !== null ? (item.skill || item.name || '') : item
    ).trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });
  return names;
}

/** Coerce a score input to 1-10 with one decimal, or null when it is not a number. */
export function clampSkillScore(raw) {
  if (raw === '' || raw === null || raw === undefined || typeof raw === 'boolean') return null;
  const numeric = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(numeric)) return null;
  return Math.round(Math.min(SKILL_SCORE_MAX, Math.max(SKILL_SCORE_MIN, numeric)) * 10) / 10;
}

/** Record an explicit score for one skill (defaults to a TPO edit). */
export function updateSkillScoreMap(map, name, raw, source = 'tpo') {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return map || {};
  return { ...(map || {}), [key]: { score: clampSkillScore(raw), source } };
}

/**
 * Merge freshly generated scores over the previous ones, keeping every value the
 * TPO typed and every number the vendor supplied. Regenerating must never
 * silently undo a manual override or take over a vendor level.
 */
export function mergeGeneratedScores(previous, generated) {
  const result = { must: { ...(previous?.must || {}) }, good: { ...(previous?.good || {}) } };
  ['must', 'good'].forEach((group) => {
    Object.entries(generated?.[group] || {}).forEach(([key, entry]) => {
      const existing = result[group][key];
      // Never let a regeneration replace a number the college owns: a TPO edit
      // always wins, and a vendor's own level is authoritative too.
      if (existing && (existing.source === 'tpo' || existing.source === 'vendor')) return;
      result[group][key] = { score: clampSkillScore(entry?.score), source: entry?.source || 'ai' };
    });
  });
  return result;
}

/**
 * Seed the editor from a saved posting. Source `stored` means "show the value but
 * do not assert it", so a later edit of the role or JD can still recalibrate it.
 */
export function scoresFromStoredSkills(mustHaveSkills, goodToHaveSkills) {
  const build = (value) => {
    const map = {};
    (Array.isArray(value) ? value : []).forEach((item) => {
      const isObject = typeof item === 'object' && item !== null;
      const name = String(isObject ? (item.skill || item.name || '') : item).trim().toLowerCase();
      if (!name) return;
      const stored = isObject ? String(item.score_source || item.scoreSource || '').toLowerCase() : '';
      // Vendor numbers and previous TPO edits are shown with their provenance so
      // the TPO can tell what the college already owns after a sync.
      map[name] = {
        score: isObject ? clampSkillScore(item.required_score) : null,
        source: ['tpo', 'vendor', 'ai'].includes(stored) ? stored : 'stored',
      };
    });
    return map;
  };
  return { must: build(mustHaveSkills), good: build(goodToHaveSkills) };
}

/** Build the skill rows the API stores, carrying only scores the TPO stands behind. */
export function buildSkillPayload(names, scoreMap) {
  return (names || []).map((name) => {
    const entry = (scoreMap || {})[String(name).trim().toLowerCase()];
    // Clamp again on the way out: a stale or hand-edited state must never put a
    // level above 10 (or below 1) into the request.
    const bounded = entry ? clampSkillScore(entry.score) : null;
    if (bounded === null) {
      return { skill: name, name };
    }
    const row = { skill: name, name, required_score: bounded };
    if (entry.source === 'tpo' || entry.source === 'ai') row.score_source = entry.source;
    return row;
  });
}
